import get from 'lodash/get';
import set from 'lodash/set';
import { utils } from 'pixi.js';
import type { ColorMap as ColorMapInstance } from '../../../core/asset/ColorMap';
import type {
    Color,
    GradientColor,
    Effect,
    Keyframe,
    Layer,
    Lottie,
    Shape,
    Style,
} from '../types/lottie';
import { EffectValueType, LayerType, ShapeType, StyleType } from '../types/lottie';
import { ColorFormat } from '../../../enums';
import { int2NormalizedRgbArray, normalizedRgb2int } from '../../videoData';
import { ErrorReporter } from '../../error-reporter';
import { BasePlugin } from './BasePlugin';

type NodePath = Array<number | string>;

type TargetPathData = {
    colorPath: NodePath;
    // alphaPath: NodePath (future support for alpha)
    colorFormat: ColorFormat;
    colorOffset: number | null; // only for colors in arrays
};

export type ColorMapData = {
    sourceColor: number;
    targetColor: number;
};

/**
 * Adds the ability to extract colors, generate color maps and replace colors in lottie json.
 */
export class ColorsPlugin extends BasePlugin {
    #colorMapObjects: ColorMapData[] = [];
    #colorMapObjectById: Record<string, ColorMapData> = {};
    #targetPathObjectsById: Record<string, TargetPathData[]> = {};
    #matteTargetLayerPaths: Set<string> = new Set();
    #nonTransparentColorMapIndices: Set<number> = new Set();

    override update(json: Lottie) {
        this.#nonTransparentColorMapIndices.clear();
        this.#extractColors(json);
    }

    get colorMaps() {
        return this.#colorMapObjects;
    }

    getNonTransparentColorMaps() {
        if (this.#nonTransparentColorMapIndices.size > 0) {
            return this.#colorMapObjects.filter((_, ind) =>
                this.#nonTransparentColorMapIndices.has(ind),
            );
        }
        return this.#colorMapObjects.filter((colorMapObj, ind) => {
            const colorId = this.#generateColorId(colorMapObj.sourceColor);
            const targetPaths = this.#targetPathObjectsById[colorId];
            if (!targetPaths || targetPaths.length === 0) {
                this.#nonTransparentColorMapIndices.add(ind);
                return true;
            }
            const allPathsInMatteLayers = targetPaths.every((targetPath) => {
                const colorPathStr = targetPath.colorPath.join('.');
                for (const mattePath of this.#matteTargetLayerPaths) {
                    if (colorPathStr === mattePath || colorPathStr.startsWith(mattePath + '.')) {
                        return true;
                    }
                }
                return false;
            });
            if (allPathsInMatteLayers) {
                return false;
            }
            this.#nonTransparentColorMapIndices.add(ind);
            return true;
        });
    }

    /**
     * @internal
     */
    get _colorMapsForUnitTest() {
        return Object.entries(this.#colorMapObjectById).map(([colorId, colorMap]) => ({
            ...colorMap,
            targetPaths: this.#targetPathObjectsById[colorId],
        }));
    }

    #generateColorId(color: number) {
        return `${color}`;
    }

    #generateColorIdFromColorMap(colorMap: ColorMapInstance) {
        const sourceColor = colorMap.sourceColor;
        return this.#generateColorId(sourceColor);
    }

    #saveColor(
        color: number,
        colorPath: NodePath,
        colorFormat: ColorFormat.HEX,
        colorOffset?: null,
    ): void;
    #saveColor(
        color: number,
        colorPath: NodePath,
        colorFormat: ColorFormat.RGB,
        colorOffset: number,
    ): void;
    #saveColor(
        color: number,
        colorPath: NodePath,
        colorFormat: ColorFormat,
        colorOffset: number = null,
    ) {
        const colorMapId = this.#generateColorId(color);
        if (!this.#colorMapObjectById[colorMapId]) {
            const colorMapObject: ColorMapData = {
                sourceColor: color,
                targetColor: color,
            };
            this.#colorMapObjectById[colorMapId] = colorMapObject;
            this.#colorMapObjects.push(colorMapObject);
        }
        if (!this.#targetPathObjectsById[colorMapId]) {
            this.#targetPathObjectsById[colorMapId] = [];
        }
        const targetPathObject: TargetPathData = {
            colorPath,
            colorFormat,
            colorOffset,
        };
        this.#targetPathObjectsById[colorMapId].push(targetPathObject);
    }

    #saveRGBColor(lottieColor: Color | GradientColor, path: NodePath, offset = 0) {
        const [r, g, b] = lottieColor.slice(offset, offset + 3);
        const color = normalizedRgb2int([r, g, b]);
        this.#saveColor(color, path, ColorFormat.RGB, offset);
    }

    #saveAnimatedColor(keyframes: Keyframe[], path: NodePath) {
        keyframes.forEach((keyframe, keyframeIndex) => {
            if (keyframe.s.length !== 3 && keyframe.s.length !== 4) {
                throw new Error(
                    `Unexpected array length of ${keyframe.s.length} for animated color`,
                );
            }
            this.#saveRGBColor(keyframe.s as Color, [...path, keyframeIndex, 's']);
        });
    }

    #saveHexColor(lottieColor: string, path: NodePath) {
        const hex = utils.string2hex(lottieColor);
        this.#saveColor(hex, path, ColorFormat.HEX);
    }

    #extractShapeColors(shape: Shape, shapePath: NodePath) {
        if (shape.hd) {
            return;
        }
        if (shape.ty === ShapeType.GradientFill) {
            /**
             * Handle gradient colors.
             * Lottie stores entire gradient in single array. This makes it difficult to show unique colors to designers
             * and map them with values set by designers. Hence need to be converted to a different structure.
             */
            if (shape.g.k.a === 1) {
                console.error('animated gradient fill is not supported');
                return;
            }
            if (shape.g.k.x) {
                return;
            }
            const numColors = shape.g.p;
            if (numColors === 0) {
                return;
            }
            for (let i = 0; i < numColors; i++) {
                const colorOffset = i * 4 + 1;
                this.#saveRGBColor(shape.g.k.k, [...shapePath, 'g', 'k', 'k'], colorOffset);
            }
        } else if (shape.ty === ShapeType.Fill || shape.ty === ShapeType.Stroke) {
            if (shape.c.x) {
                return;
            }
            if (shape.c.a === 1) {
                this.#saveAnimatedColor(shape.c.k, [...shapePath, 'c', 'k']);
            } else {
                this.#saveRGBColor(shape.c.k, [...shapePath, 'c', 'k']);
            }
        } else if (shape.ty === ShapeType.Group) {
            shape.it.forEach((shape, shapeIndex) => {
                this.#extractShapeColors(shape, [...shapePath, 'it', shapeIndex]);
            });
        }
    }

    #extractEffectColors(effect: Effect, effectPath: NodePath) {
        effect.ef.forEach((effectValue, effectValueIndex) => {
            if (effectValue.ty === EffectValueType.Color) {
                if (effectValue.v.a === 1) {
                    console.error('animating effect colors is not supported');
                    return;
                }
                if (effectValue.v.x) {
                    return;
                }
                const path = [...effectPath, 'ef', effectValueIndex, 'v', 'k'];
                this.#saveRGBColor(effectValue.v.k, path);
            }
        });
    }

    #extractStyleColors(style: Style, effectPath: NodePath) {
        if (style.ty === StyleType.BevelEmboss) {
            console.error('color mapping bevel emboss style is not yet supported');
        } else if (style.ty === StyleType.GradientOverlay) {
            console.error('color mapping gradient overlay style is not yet supported');
        } else {
            if (style.c.a === 1) {
                console.error('animating style colors is not supported');
                return;
            }
            if (style.c.x) {
                return;
            }
            this.#saveRGBColor(style.c.k, [...effectPath, 'c', 'k']);
        }
    }

    #isMatteTargetLayer(layer: Layer): boolean {
        return layer.td !== undefined && layer.td !== 0;
    }

    #extractLayerColors(layer: Layer, layerPath: NodePath) {
        if (layer.hd) {
            return;
        }
        if (layer.ty === LayerType.SolidColor) {
            this.#saveHexColor(layer.sc, [...layerPath, 'sc']);
        } else if (layer.ty === LayerType.Shape) {
            layer.shapes.forEach((shape, shapeIndex) => {
                this.#extractShapeColors(shape, [...layerPath, 'shapes', shapeIndex]);
            });
        }
        if (layer.ef && layer.ef.length > 0) {
            layer.ef.forEach((effect, effectIndex) => {
                this.#extractEffectColors(effect, [...layerPath, 'ef', effectIndex]);
            });
        }
        if (layer.sy && layer.sy.length > 0) {
            layer.sy.forEach((style, styleIndex) => {
                this.#extractStyleColors(style, [...layerPath, 'sy', styleIndex]);
            });
        }
    }

    #extractColors(json: Lottie) {
        this.#matteTargetLayerPaths.clear();

        if (Array.isArray(json.layers)) {
            json.layers.forEach((layer, layerIndex) => {
                if (this.#isMatteTargetLayer(layer)) {
                    this.#matteTargetLayerPaths.add(`layers.${layerIndex}`);
                }
                this.#extractLayerColors(layer, ['layers', layerIndex]);
            });
        }
        if (Array.isArray(json.assets)) {
            json.assets.forEach((asset, assetIndex) => {
                if ('layers' in asset && Array.isArray(asset.layers)) {
                    asset.layers.forEach((layer, layerIndex) => {
                        if (this.#isMatteTargetLayer(layer)) {
                            this.#matteTargetLayerPaths.add(
                                `assets.${assetIndex}.layers.${layerIndex}`,
                            );
                        }
                        this.#extractLayerColors(layer, [
                            'assets',
                            assetIndex,
                            'layers',
                            layerIndex,
                        ]);
                    });
                }
            });
        }
    }

    #getMatchedColorForColorMap(colorMap: ColorMapInstance): ColorMapData | undefined {
        const colorId = this.#generateColorIdFromColorMap(colorMap);
        return this.#colorMapObjectById[colorId];
    }

    #getMatchedTargetPathsForColorMap(colorMap: ColorMapInstance): TargetPathData[] | undefined {
        const colorId = this.#generateColorIdFromColorMap(colorMap);
        return this.#targetPathObjectsById[colorId];
    }

    shouldReplaceColor(colorMap: ColorMapInstance) {
        const matchedColor = this.#getMatchedColorForColorMap(colorMap);
        if (!matchedColor) {
            return false;
        }
        if (matchedColor.targetColor === colorMap.targetColor) {
            return false; // this color is already replaced in current value
        }
        const matchedTargetPaths = this.#getMatchedTargetPathsForColorMap(colorMap);
        if (!matchedTargetPaths || matchedTargetPaths.length === 0) {
            return false;
        }
        return true;
    }

    replaceColor(state: Lottie, colorMap: ColorMapInstance) {
        const matchedColor = this.#getMatchedColorForColorMap(colorMap);
        const targetPaths = this.#getMatchedTargetPathsForColorMap(colorMap);
        const targetColor = colorMap.targetColor;
        for (const targetPath of targetPaths) {
            const { colorPath, colorFormat, colorOffset } = targetPath;
            if (!get(state, colorPath)) {
                const e = new Error('ColorsPlugin tried to replace color for an invalid path');
                console.warn(e, colorPath);
                ErrorReporter.capture(e, {
                    extra: { colorPath, colorFormat, colorOffset },
                });
                continue;
            }
            if (colorFormat === ColorFormat.HEX) {
                set(state, colorPath, utils.hex2string(targetColor));
            } else if (colorFormat === ColorFormat.RGB) {
                if (colorOffset === null) {
                    throw new Error(`color offset missing to replace RGB color at ${colorPath}`);
                }
                const colorsArray: number[] = get(state, colorPath);
                const updatedColor = int2NormalizedRgbArray(targetColor);
                colorsArray.splice(colorOffset, updatedColor.length, ...updatedColor);
            } else {
                throw new Error(`Unsupported colorFormat: ${colorFormat}`);
            }
        }
        matchedColor.targetColor = targetColor;
    }
}
